import React from "react";

import { useNavigation } from "@/providers";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { useDataset } from "@/shared/domain/datasets/hooks";
import { IngestionStatus } from "@/shared/domain/files";
import { useFile } from "@/shared/domain/files/hooks";
import { useMessagePathById } from "@/shared/domain/topics/hooks/useMessagePath.ts";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic.ts";
import { getWorkspaceInput } from "@/shared/domain/workspaces/WorkspaceBuilder";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";

import { HighlightedSpan } from "./HighlightedSpan";

interface AISummaryMarkdownLinkProps {
  datasetId?: string;
  fileId?: string;
  topicId?: string;
  messagePathId?: string;
  time?: bigint;
  text: string;
  auto?: boolean;
  autoNavProps?: {
    enabled: boolean;
    dedupeKey: string;
  };
  onLinkClick?: () => void;
}

export const AISummaryMarkdownLink: React.FC<AISummaryMarkdownLinkProps> = ({
  datasetId,
  fileId,
  topicId,
  messagePathId,
  time,
  text,
  auto,
  autoNavProps,
  onLinkClick,
}) => {
  const { goto } = useNavigation();
  const dataset = useDataset(datasetId);
  const file = useFile(fileId);
  const topic = useTopicById(topicId);
  const messagePath = useMessagePathById(messagePathId);
  const messagePathTopic = useTopicById(messagePath.data?.topic_id);

  let linkProps:
    | {
        tooltipText: string;
        onClick: () => void;
      }
    | undefined = undefined;

  if (messagePath.data && messagePathTopic.data) {
    const panelType = determineDefaultPanelType(
      messagePath.data.canonical_data_type,
    );
    linkProps = {
      tooltipText: `Visualize: ${messagePathTopic.data.topic_name}.${messagePath.data.message_path}`,
      onClick: () => {
        onLinkClick?.();
        goto.visualize({
          dataSource: {
            files: [messagePathTopic.data.association.association_id],
          },
          params: {
            layout: getWorkspaceInput(
              panelType,
              messagePathTopic.data,
              messagePath.data,
            ),
            t: time,
          },
        });
      },
    };
  } else if (topic.data) {
    linkProps = {
      tooltipText: `Visualize: ${topic.data.topic_name}`,
      onClick: () => {
        onLinkClick?.();
        goto.visualize({
          dataSource: {
            files: [topic.data.association.association_id],
          },
          params: {
            filterTerm: topic.data.topic_name,
            t: time,
          },
        });
      },
    };
  } else if (file.data) {
    linkProps = {
      tooltipText: `View: ${file.data.relative_path}`,
      onClick: () => {
        onLinkClick?.();
        if (file.data.ingestion_status !== IngestionStatus.NotIngested) {
          goto.visualize({
            dataSource: {
              files: [file.data.file_id],
            },
            params: {
              t: time,
            },
          });
        } else {
          goto.file({
            fileId: file.data.file_id,
          });
        }
      },
    };
  } else if (dataset.data) {
    linkProps = {
      tooltipText: `View: '${dataset.data.dataset_id}'`,
      onClick: () => {
        onLinkClick?.();
        goto.dataset({ datasetId: dataset.data.dataset_id });
      },
    };
  }

  React.useEffect(() => {
    if (!auto || !autoNavProps || !autoNavProps.enabled || !linkProps) {
      return;
    }

    const entityId = datasetId ?? fileId ?? topicId ?? messagePathId;

    if (!entityId) {
      return;
    }

    let fullDedupeKey = entityId;
    if (time) {
      fullDedupeKey += `-${time}`;
    }

    fullDedupeKey += autoNavProps.dedupeKey;

    if (sessionStorage.getItem(fullDedupeKey) === "followed") {
      return;
    }

    sessionStorage.setItem(fullDedupeKey, "followed");
    linkProps.onClick();
  }, [
    autoNavProps,
    auto,
    linkProps,
    datasetId,
    fileId,
    topicId,
    messagePathId,
    time,
  ]);

  // If the link doesn't resolve to anything valid, just render the text
  if (!linkProps) {
    return <span>{text}</span>;
  }

  return (
    <RobotoTooltip title={linkProps.tooltipText}>
      <HighlightedSpan onClick={linkProps.onClick}>{text}</HighlightedSpan>
    </RobotoTooltip>
  );
};
